import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import AuthService from "../AuthService";
import { ParentPageHeader, PortalLoading, useParentPortal } from "./parentPortalUtils";

function resolveMediaUrl(value) {
  if (!value) return "";
  if (/^https?:\/\//.test(value)) return value;
  const apiBase = axiosInstance.defaults?.baseURL || "";
  const backendBase = apiBase.replace(/\/api\/?$/, "").replace(/\/$/, "");
  return value.startsWith("/") && backendBase ? `${backendBase}${value}` : value;
}

function ParentProfilePage() {
  const { data, loading, reload } = useParentPortal("parent-portal/profile/");
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data?.parent) setForm(data.parent);
  }, [data]);

  const preview = useMemo(() => {
    if (photo) return URL.createObjectURL(photo);
    return resolveMediaUrl(form.profile_picture_url);
  }, [photo, form.profile_picture_url]);

  useEffect(() => () => {
    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
  }, [preview]);

  if (loading && !data) return <PortalLoading />;

  const saveProfile = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const payload = new FormData();
      ["phone","alternative_phone","address","city","province","country","occupation","employer","work_phone","relationship_notes"]
        .forEach((field) => payload.append(field, form[field] || ""));
      if (photo) payload.append("profile_picture", photo);

      const response = await axiosInstance.patch("parent-portal/profile/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const updated = response.data.parent || {};
      const picture = resolveMediaUrl(updated.profile_picture_url);
      AuthService.updateCurrentUser({
        full_name: updated.full_name,
        email: updated.email,
        phone: updated.phone,
        profile_picture: picture,
        profile_picture_url: picture,
      });
      setForm(updated);
      setPhoto(null);
      toast.success("Profile updated.");
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="container-fluid py-4">
      <ParentPageHeader title="My Profile" subtitle="Keep your photograph and contact information current." icon="bi-person-circle" actions={<button className="btn btn-light border" onClick={reload}>Refresh</button>} />
      <div className="card border-0 shadow-sm">
        <form className="card-body" onSubmit={saveProfile}>
          <div className="row g-4">
            <div className="col-lg-3">
              <div className="border rounded-4 p-3 text-center bg-light">
                {preview ? <img src={preview} alt="Parent profile" className="rounded-circle border mb-3" style={{width:150,height:150,objectFit:"cover"}} /> :
                  <div className="rounded-circle border bg-white d-inline-flex align-items-center justify-content-center mb-3" style={{width:150,height:150}}><i className="bi bi-person fs-1 text-muted"></i></div>}
                <label className="btn btn-outline-success w-100"><i className="bi bi-camera me-2"></i>Change Profile Picture<input type="file" accept="image/*" className="d-none" onChange={(e)=>setPhoto(e.target.files?.[0]||null)} /></label>
                <small className="text-muted d-block mt-2">Use a clear photo so authorised school staff can identify you.</small>
              </div>
            </div>
            <div className="col-lg-9"><div className="row g-3">
              <Readonly label="Full Name" value={form.full_name} />
              <Readonly label="Email" value={form.email} />
              <Field label="Phone" value={form.phone} onChange={(v)=>update("phone",v)} />
              <Field label="Alternative Phone" value={form.alternative_phone} onChange={(v)=>update("alternative_phone",v)} />
              <Field label="Occupation" value={form.occupation} onChange={(v)=>update("occupation",v)} />
              <Field label="Employer" value={form.employer} onChange={(v)=>update("employer",v)} />
              <Field label="Work Phone" value={form.work_phone} onChange={(v)=>update("work_phone",v)} />
              <Field label="City" value={form.city} onChange={(v)=>update("city",v)} />
              <Field label="Province" value={form.province} onChange={(v)=>update("province",v)} />
              <Field label="Country" value={form.country} onChange={(v)=>update("country",v)} />
              <div className="col-12"><label className="form-label fw-semibold">Address</label><textarea className="form-control" rows="2" value={form.address||""} onChange={(e)=>update("address",e.target.value)} /></div>
              <div className="col-12"><label className="form-label fw-semibold">Relationship Notes</label><textarea className="form-control" rows="2" value={form.relationship_notes||""} onChange={(e)=>update("relationship_notes",e.target.value)} /></div>
              <div className="col-12"><button className="btn btn-success" disabled={saving}>{saving?"Saving...":"Save Profile"}</button></div>
            </div></div>
          </div>
        </form>
      </div>
    </div>
  );
}
function Field({label,value,onChange}) { return <div className="col-md-4"><label className="form-label fw-semibold">{label}</label><input className="form-control" value={value||""} onChange={(e)=>onChange(e.target.value)} /></div>; }
function Readonly({label,value}) { return <div className="col-md-4"><label className="form-label fw-semibold">{label}</label><input className="form-control" value={value||""} disabled /></div>; }
export default ParentProfilePage;
